import type { CSSProperties } from 'react'
import type { PeriodOption } from '../provider/providerClient'
import type { SliceAxes, SliceDepth, SliceScope, SliceType } from '../analytics/slice'

/**
 * THE PAGE-LEVEL CONTROL BAR — v1's shape, and the reason its dashboard reads as slicing rather
 * than navigating. FOUR AXES THAT ALL COMPOSE, driving every panel on the page at once. Change one
 * and the headline figures, the share and both leaderboards re-cut together.
 *
 * ★★ THE AXES ARE PAGE STATE, NOT PANEL STATE. Per-panel filters would let a supplier compare two
 * numbers answering about different windows without noticing — which is exactly the class of
 * defect that bit live v1: its two tabs share period state but each draws its own Period select,
 * so setting the period on one tab silently re-writes the money figures on the other while both
 * the heading and that tab's own select still name the original quarter (£29.57 vs £52.42 for the
 * same org, verified 2026-07-23). Everything here reads and writes ONE state object in the shell.
 *
 * ★ Period options come from the SERVER (`GET /provider/periods`) because the labels are derived
 * from the same quarter arithmetic that resolves the windows; a client-side list would drift the
 * first time the two disagreed about which quarter "now" is in.
 *
 * ⚠ NATIVE <select>, DELIBERATELY. On a phone it opens the OS picker — better than any custom
 * dropdown we would write, and free.
 */

const label: CSSProperties = { fontSize: 'var(--fs-sm)', color: 'var(--muted-foreground)', flex: '0 0 auto' }

const select: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '5px 10px',
  fontSize: 'var(--fs-sm)',
  fontFamily: 'var(--font-standard)',
  background: 'var(--card)',
  color: 'var(--foreground)',
}

const TYPES: { value: SliceType; label: string }[] = [
  { value: 'all', label: 'All content' },
  { value: 'films', label: 'Only films' },
  { value: 'series', label: 'Only series' },
  { value: 'supporting', label: 'Only supporting material' },
]

const DEPTHS: { value: SliceDepth; label: string }[] = [
  { value: 'title', label: 'All seasons' },
  { value: 'season', label: 'Per season' },
  { value: 'episode', label: 'Per episode' },
]

const SCOPES: { value: SliceScope; label: string }[] = [
  { value: 'yours', label: 'Share of yours' },
  { value: 'platform', label: 'Share of Yoreflix' },
]

function Axis<T extends string>({
  id,
  name,
  value,
  options,
  onChange,
}: {
  id: string
  name: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <span className="dash-axis">
      <label style={label} htmlFor={id}>
        {name}
      </label>
      <select id={id} style={select} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </span>
  )
}

export function FilterBar({
  periods,
  period,
  axes,
  onPeriod,
  onAxes,
}: {
  periods: PeriodOption[]
  period: string
  axes: SliceAxes
  onPeriod: (key: string) => void
  onAxes: (axes: SliceAxes) => void
}) {
  return (
    <div className="dash-axes">
      <Axis
        id="axis-period"
        name="Period"
        value={period}
        options={periods.map((p) => ({ value: p.Key, label: p.Label }))}
        onChange={onPeriod}
      />
      <Axis
        id="axis-type"
        name="Filter"
        value={axes.type}
        options={TYPES}
        onChange={(type) => onAxes({ ...axes, type })}
      />
      {/* ⚠ THE DEPTH AXIS ONLY EXISTS FOR SERIES, exactly as v1 hides its `Series :` select unless
          the filter is "Only series". A depth control over a list of films is a control that
          changes nothing, and a control that changes nothing teaches you to distrust the rest. */}
      {axes.type === 'series' && (
        <Axis
          id="axis-depth"
          name="Series"
          value={axes.depth}
          options={DEPTHS}
          onChange={(depth) => onAxes({ ...axes, depth })}
        />
      )}
      {/* ★★ THE SCOPE AXIS, HONESTLY — see `shareOf`. v1's "Whole Yoreflix platform" tab drew every
          other supplier's bar unlabelled at 20% opacity; we will not, because shape and magnitude
          leak even without a name. What survives is the useful half: the same rows, re-based
          against everything watched on Yoreflix instead of against your own total. */}
      <Axis
        id="axis-scope"
        name="Compare"
        value={axes.scope}
        options={SCOPES}
        onChange={(scope) => onAxes({ ...axes, scope })}
      />
    </div>
  )
}
